import { joinUrlPath } from "@dx/components/utils";
import { UserType } from "./services/users.service.types";
import { debug } from "../utils";

const navigateAsClientRoute = "vc";
const navigateAsPublicRoute = "vp";

export function getNavigateAs(): UserType | undefined {
  const matcher = new RegExp(
    `^${joinUrlPath(
      process.env.PUBLIC_URL ?? "",
      `(${navigateAsClientRoute}|${navigateAsPublicRoute})`
    )}`
  );

  const routeBasepath = document.location.pathname.match(matcher)?.[1];

  const userType = [UserType.Public, UserType.Client].find((navigationAs) => {
    const { navigateAsRoutePrefix } =
      navigateAsConfig.find((u) => u.userType === navigationAs) ?? {};
    return routeBasepath === navigateAsRoutePrefix;
  });

  debug(
    `UserType is ${userType}. basepath = ${routeBasepath}, pathname = ${document.location.pathname}`
  );
  return userType;
}

const navigateAsConfig = [
  {
    navigateAsRoutePrefix: navigateAsPublicRoute,
    userType: UserType.Public,
    audience: "pub",
  },
  {
    navigateAsRoutePrefix: navigateAsClientRoute,
    userType: UserType.Client,
    audience: "ext",
  },
];

export const mapAudienceToUserType = (audience: string) =>
  navigateAsConfig.find((x) => x.audience === audience)?.userType;

// in the URL : /vp/magic
// routeBaseBath => /vp/
export function getNavigateAsRoutePrefix() {
  return (
    (
      navigateAsConfig.find(({ userType }) => userType === getNavigateAs()) ??
      {}
    )?.navigateAsRoutePrefix ?? ""
  );
}

export function ensureUserCanImpersonate({ audience }: { audience: string }) {
  // if /vc make sure the audience is 'client'
  // if /vp make sure the audience is 'public'
  // if not set navigate as to undefined
  const expectedAudience = navigateAsConfig.find(
    (x) => x.audience === audience
  );
  if (
    (expectedAudience?.navigateAsRoutePrefix ?? "") !==
    getNavigateAsRoutePrefix()
  ) {
    debug(
      `Clearing navigate as, expected prefix: ${
        expectedAudience?.navigateAsRoutePrefix
      }, actual prefix: ${getNavigateAsRoutePrefix()}`
    );
    setNavigateAs(undefined);
  }
}

export function setNavigateAs(as: UserType | undefined) {
  debug(`Setting navigate as = ${as}`);
  setNavigateAsCookie(as);

  const url = new URL(document.location.href);

  // pages that are not found are redirected to /404?from=super-secret-admin-page
  const from = url.searchParams.get("from");
  if (from) {
    // url.pathname sets the starting '/' if necessary
    url.pathname = from;
    // don't need this anymore
    url.searchParams.delete("from");
  }

  // clean previous navigate-as from pathname
  for (const { navigateAsRoutePrefix } of navigateAsConfig) {
    const navigateAsRouteMatcher = new RegExp(
      `^${joinUrlPath(
        process.env.PUBLIC_URL ?? "",
        navigateAsRoutePrefix
      )}(/|$)`
    );
    if (url.pathname.match(navigateAsRouteMatcher)) {
      // /{PUBLIC_URL}/vp => /PUBLIC_URL/
      // /vp/explore => /explore/
      // /vp => /
      // /vp/ => /

      url.pathname = url.pathname.replace(
        navigateAsRouteMatcher,
        joinUrlPath(process.env.PUBLIC_URL ?? "", "/")
      );
    }
  }

  // get route for user type
  const { navigateAsRoutePrefix } =
    navigateAsConfig.find(({ userType }) => userType === as) ?? {};

  // set the URL ! :D
  if (navigateAsRoutePrefix) {
    url.pathname = joinUrlPath(
      process.env.PUBLIC_URL ?? "",
      `${navigateAsRoutePrefix}${url.pathname.replace(
        process.env.PUBLIC_URL ?? "",
        ""
      )}`
    );
  }
  window.history.pushState({}, "", url.href);
}

export function setNavigateAsCookie(userType?: UserType) {
  // cookie used by some services to handle impersonation
  const cookieName = "dx-navigate-as";

  if (!userType) {
    debug("Removing dx-navigate-as cookie");
    const expired = "expires=Thu, 01 Jan 1970 00:00:01 GMT";
    document.cookie = `${cookieName}=; path=/; ${expired}`;
  } else {
    debug(`Adding dx-navigate-as cookie=${userType}`);
    document.cookie = `${cookieName}=${userType}; path=/`;
  }
}
